import React from "react";
import { Label } from "../ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Button } from "../ui/button";
import { AlertTriangle, Info, Settings2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { getProjectFamilies } from "../../lib/wizardCalc";

// Phase B.1.6 — Selection Criteria tab: FACB Strategy, ACB Strategy.
// Phase B.1.7 — Speed Ceiling (MED/HIGH) replaced with full LOW/MED/HIGH fan-speed
// radio (acb_fan_speed). Max/Fixed water flow moved to DesignConditionsTab § Water.
// Tab never blocks "Next" — all fields have defaults.
//
// AHRI 1335 single-basis lock: the Capacity Basis selector was removed.
// Display always uses room-delivered (Q_r, AHRI 1335). The payload hardcodes
// capacity_basis = "room_load" in wizardCalc.js regardless of any saved value.

export default function SelectionCriteriaTab({
  project,
  updateProject,
  rooms = [],
  isSI = false,
}) {
  const advanced = project.advanced || {};

  const setFacbStrategy = (v) =>
    updateProject({ ...project, facb_strategy: v });
  const setAcbStrategy = (v) =>
    updateProject({ ...project, acb_strategy: v });
  const setAdv = (k, v) =>
    updateProject({ ...project, advanced: { ...advanced, [k]: v } });
  const setWaterLimit = (k, v) =>
    updateProject({ ...project, [k]: v });

  const { hasFACB, hasACB, hasEWS } = getProjectFamilies(rooms);
  const ewsOnly = hasEWS && !hasFACB && !hasACB;

  const facbStrategy = project.facb_strategy || "lowest_speed";
  const acbStrategy = project.acb_strategy || "given_flow_given_sp";

  // Full LOW/MED/HIGH fan-speed selector (replaces the MED/HIGH ceiling from B.1.6).
  const acbFanSpeed = advanced.acb_fan_speed || "MED";

  // B.6 — water-side limit fields (project-level, same as capacity_basis).
  const maxWaterDp = project.max_water_dp ?? "";
  const flowCapMode = project.water_flow_cap_mode || "off";
  const flowCapValue = project.water_flow_cap_value ?? "";

  return (
    <div className="space-y-3" data-testid="wizard-criteria-tab">

      {/* Capacity Basis selector removed — AHRI 1335 (Q_r, room-delivered) is the
          only display basis. wizardCalc.js hardcodes capacity_basis = "room_load"
          in all payloads. Water-side plant-sizing data remains in the per-room
          Water Side section of the detail report. */}

      {/* EWS-only fallback note — hide strategy cards 2-4 entirely */}
      {ewsOnly && (
        <p
          className="text-xs text-muted-foreground px-1"
          data-testid="wizard-criteria-ews-note"
        >
          Selection criteria below don&apos;t apply to EWS (Model N / N-f). System
          selection uses the full-system Model N calculator.
        </p>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* 2-4. Strategy cards — hidden for EWS-only projects                 */}
      {/* ------------------------------------------------------------------ */}
      {!ewsOnly && (
        <>
          {/* -------- FACB Fan Speed (LOW / MED / HIGH) ------------------- */}
          {hasFACB && (
            <Card data-testid="wizard-criteria-speed-ceiling-card">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
                  FACB Fan Speed
                  <span className="text-[10px] font-normal text-muted-foreground">
                    (Models S, S-HW, O-1, O-2)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 space-y-2">
                <p className="text-[10px] text-muted-foreground">
                  Fan speed at which the engine rates FACB capacity. Higher speeds deliver more
                  cooling but increase induction noise. Length escalates automatically if the
                  chosen speed cannot meet the target load.
                </p>
                <div
                  className="flex gap-1"
                  role="group"
                  aria-label="FACB fan speed"
                  data-testid="wizard-criteria-speed-ceiling"
                >
                  {["LOW", "MED", "HIGH"].map((speed) => {
                    const active = acbFanSpeed === speed;
                    return (
                      <Button
                        key={speed}
                        size="sm"
                        variant={active ? "default" : "outline"}
                        className={active ? "bg-primary text-primary-foreground text-xs" : "text-xs"}
                        onClick={() => setAdv("acb_fan_speed", speed)}
                        data-testid={`wizard-criteria-speed-${speed.toLowerCase()}`}
                      >
                        {speed}
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* -------- FACB Strategy --------------------------------------- */}
          {hasFACB && (
            <Card data-testid="wizard-criteria-facb-strategy-card">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
                  FACB Strategy
                  <span className="text-[10px] font-normal text-muted-foreground">
                    Fan-Assisted Active Chilled Beam (S, S-HW, O-1, O-2)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 space-y-3">
                <RadioGroup
                  value={facbStrategy}
                  onValueChange={setFacbStrategy}
                  className="gap-2"
                  data-testid="wizard-criteria-facb-strategy"
                >
                  <div className="flex items-start gap-2">
                    <RadioGroupItem
                      value="lowest_speed"
                      id="facb-lowest"
                      data-testid="wizard-criteria-facb-lowest"
                      className="mt-0.5"
                    />
                    <div>
                      <Label htmlFor="facb-lowest" className="text-xs font-medium cursor-pointer">
                        A — Lowest fan speed (water modulates within max)
                      </Label>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Engine walks LOW→MED→HIGH; water flow is modulated within the max cap to meet the target.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem
                      value="given_flow"
                      id="facb-given-flow"
                      data-testid="wizard-criteria-facb-given-flow"
                      className="mt-0.5"
                    />
                    <div>
                      <Label htmlFor="facb-given-flow" className="text-xs font-medium cursor-pointer">
                        B — Given water flow (length escalates)
                      </Label>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Engine holds the water flow fixed and finds the shortest length that meets the target.
                        Speed still cascades LOW→MED→HIGH.
                      </p>
                    </div>
                  </div>
                </RadioGroup>

                {/* Max/Fixed water flow is set in Design Conditions → Water section (B.1.7) */}
              </CardContent>
            </Card>
          )}

          {/* -------- ACB Strategy --------------------------------------- */}
          {hasACB && (
            <Card data-testid="wizard-criteria-acb-strategy-card">
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                  <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
                  ACB Strategy
                  <span className="text-[10px] font-normal text-muted-foreground">
                    Active Chilled Beam / Induction (I, L, C, C1, H)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 space-y-2">
                <RadioGroup
                  value={acbStrategy}
                  onValueChange={setAcbStrategy}
                  className="gap-2"
                  data-testid="wizard-criteria-acb-strategy"
                >
                  <div className="flex items-start gap-2">
                    <RadioGroupItem
                      value="given_flow_given_sp"
                      id="acb-a"
                      data-testid="wizard-criteria-acb-given-flow-given-sp"
                      className="mt-0.5"
                    />
                    <Label htmlFor="acb-a" className="text-xs font-medium cursor-pointer">
                      (a) Given PA flow at given static pressure
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem
                      value="given_flow_lowest_sp"
                      id="acb-b"
                      data-testid="wizard-criteria-acb-given-flow-lowest-sp"
                      className="mt-0.5"
                    />
                    <Label htmlFor="acb-b" className="text-xs font-medium cursor-pointer">
                      (b) Given PA flow for lowest static pressure
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem
                      value="lowest_flow_given_sp"
                      id="acb-c"
                      data-testid="wizard-criteria-acb-lowest-flow-given-sp"
                      className="mt-0.5"
                    />
                    <Label htmlFor="acb-c" className="text-xs font-medium cursor-pointer">
                      (c) Lowest PA flow at given static pressure
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem
                      value="lowest_flow_lowest_sp"
                      id="acb-d"
                      data-testid="wizard-criteria-acb-lowest-flow-lowest-sp"
                      className="mt-0.5"
                    />
                    <Label htmlFor="acb-d" className="text-xs font-medium cursor-pointer">
                      (d) Lowest PA flow and lowest static pressure
                    </Label>
                  </div>
                </RadioGroup>
                <p className="text-[10px] text-muted-foreground">
                  PA flow is the room&apos;s OA Flow input. Static pressure is set per-room
                  in the Summary tab (PA Static Pressure column).
                </p>
              </CardContent>
            </Card>
          )}

          {/* -------- Mixed-family amber notice -------------------------- */}
          {hasFACB && hasACB && (
            <div
              className="flex items-start gap-2 rounded border border-amber-400 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
              data-testid="wizard-criteria-mixed-family-notice"
              role="alert"
            >
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-500" aria-hidden />
              <span>
                This project contains both FACB and ACB units. Both strategies above apply — each to its respective rooms.
              </span>
            </div>
          )}
        </>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Water-Side Limits (all family types)                               */}
      {/* ------------------------------------------------------------------ */}
      <Card data-testid="wizard-criteria-water-limits-card">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="flex items-center gap-2 text-sm font-semibold">
            <Settings2 className="h-3.5 w-3.5 text-muted-foreground" />
            Water-Side Limits
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3 space-y-4">

          {/* ---- No. of Coil Pipes ---- */}
          {/* Placeholder — value flows into ordering codes only; no calculation impact yet. */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">No. of Coil Pipes</Label>
            <RadioGroup
              value={String(advanced.no_coil_pipes ?? 2)}
              onValueChange={(v) => setAdv("no_coil_pipes", parseInt(v, 10))}
              className="flex gap-4"
              aria-label="Number of coil pipes"
              data-testid="wizard-criteria-no-coil-pipes"
            >
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="2" id="coil-pipes-2" data-testid="wizard-criteria-coil-pipes-2" />
                <Label htmlFor="coil-pipes-2" className="text-xs cursor-pointer">2-pipe</Label>
              </div>
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="4" id="coil-pipes-4" data-testid="wizard-criteria-coil-pipes-4" />
                <Label htmlFor="coil-pipes-4" className="text-xs cursor-pointer">4-pipe</Label>
              </div>
            </RadioGroup>
          </div>

          {/* ---- ACB Coil Circuits Override ---- */}
          {/* Default rule for the ACB family (C / C1 / D / H / I-* / L-*):
              ≤4ft → 1-cct only; 5-6ft → 2-cct only. Override pins all ACB
              selections to a single circuit count regardless of length. */}
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Label className="text-xs font-medium">ACB Coil Circuits</Label>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info
                      className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                      aria-label="ACB coil circuits explanation"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    Applies to ACB-family models (C, C1, D, H, I-*, L-*).
                    Auto = ≤4ft uses 1-cct, &gt;4ft uses 2-cct.
                    Force overrides the rule for all lengths.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <RadioGroup
              value={advanced.acb_circuit_override ?? "auto"}
              onValueChange={(v) => setAdv("acb_circuit_override", v)}
              className="flex gap-4 flex-wrap"
              aria-label="ACB coil circuits"
              data-testid="wizard-criteria-acb-circuit-override"
            >
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="auto" id="acb-cct-auto" data-testid="wizard-criteria-acb-cct-auto" />
                <Label htmlFor="acb-cct-auto" className="text-xs cursor-pointer">Auto (rule)</Label>
              </div>
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="force_1cct" id="acb-cct-1" data-testid="wizard-criteria-acb-cct-1" />
                <Label htmlFor="acb-cct-1" className="text-xs cursor-pointer">Force 1-cct</Label>
              </div>
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="force_2cct" id="acb-cct-2" data-testid="wizard-criteria-acb-cct-2" />
                <Label htmlFor="acb-cct-2" className="text-xs cursor-pointer">Force 2-cct</Label>
              </div>
            </RadioGroup>
          </div>

          {/* ---- Max Water Pressure Drop ---- */}
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="max-water-dp" className="text-xs font-medium">
                Max Water Pressure Drop
              </Label>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info
                      className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                      aria-label="Max water pressure drop explanation"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    Hard limit on water-side pressure drop across the coil. Defaults to 20 ftH2O
                    when blank. For O-2 serial piping the limit applies to combined Op + ACB DP;
                    otherwise per-coil.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="max-water-dp"
                type="number"
                min="0"
                step="0.5"
                placeholder="20"
                value={maxWaterDp}
                onChange={(e) => {
                  const raw = e.target.value;
                  setWaterLimit("max_water_dp", raw === "" ? null : raw);
                }}
                className="h-8 w-28 rounded border border-input bg-background px-2 text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
                data-testid="wizard-criteria-max-water-dp"
              />
              <span className="text-xs text-muted-foreground">ftH2O</span>
            </div>
          </div>

          {/* ---- Max Water Flow Cap ---- */}
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label className="text-xs font-medium">Max Water Flow Cap</Label>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info
                      className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                      aria-label="Max water flow cap explanation"
                    />
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-xs text-xs">
                    Constrains water flow per coil during engine selection. Off = hydraulic ceiling
                    only (2.4 / 4.8 GPM). Max GPM = absolute hard cap. Max GPM/Ton = flow scaled
                    by room target load.
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>

            {/* Mode radio buttons */}
            <div
              className="flex gap-1"
              role="group"
              aria-label="Water flow cap mode"
              data-testid="wizard-criteria-flow-cap-mode"
            >
              {[
                { value: "off", label: "Off" },
                { value: "max_gpm", label: "Max GPM" },
                { value: "max_gpm_per_ton", label: "Max GPM/Ton" },
              ].map(({ value, label }) => {
                const active = flowCapMode === value;
                return (
                  <Button
                    key={value}
                    size="sm"
                    variant={active ? "default" : "outline"}
                    className={active ? "bg-primary text-primary-foreground text-xs" : "text-xs"}
                    onClick={() => {
                      // Single update — two back-to-back setWaterLimit calls
                      // both read the same `project` closure, so the second
                      // overwrites the first (Off → mode never persisted).
                      if (value === "off") {
                        updateProject({
                          ...project,
                          water_flow_cap_mode: "off",
                          water_flow_cap_value: null,
                        });
                      } else {
                        setWaterLimit("water_flow_cap_mode", value);
                      }
                    }}
                    data-testid={`wizard-criteria-flow-cap-${value}`}
                  >
                    {label}
                  </Button>
                );
              })}
            </div>

            {/* Value input — hidden when mode is Off */}
            {flowCapMode !== "off" && (
              <div className="flex items-center gap-2">
                <input
                  id="flow-cap-value"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder={flowCapMode === "max_gpm" ? "4.0" : "2.4"}
                  value={flowCapValue}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setWaterLimit("water_flow_cap_value", raw === "" ? null : raw);
                  }}
                  className="h-8 w-28 rounded border border-input bg-background px-2 text-xs tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
                  data-testid="wizard-criteria-flow-cap-value"
                  aria-label={flowCapMode === "max_gpm" ? "Max GPM value" : "Max GPM per ton value"}
                />
                <span className="text-xs text-muted-foreground">
                  {flowCapMode === "max_gpm" ? "GPM" : "GPM/Ton"}
                </span>
                {flowCapMode === "max_gpm_per_ton" && (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info
                          className="h-3.5 w-3.5 text-muted-foreground cursor-help"
                          aria-label="GPM/Ton explanation"
                        />
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs">
                        Engine multiplies by target cooling load (BTU/h ÷ 12,000) to derive the
                        per-room GPM cap. Disabled in Max-Performance mode (no target set).
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                {flowCapMode === "max_gpm" && (
                  <span className="text-[10px] text-muted-foreground">Absolute hard cap per coil</span>
                )}
              </div>
            )}
          </div>

        </CardContent>
      </Card>
    </div>
  );
}
